import { useLocation, Navigate, Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { getArticle } from "@/lib/data";
import { SITE_URL } from "@/lib/site";
import { useLang } from "@/i18n";
import { ArrowLeft, ArrowRight } from "lucide-react";

const DATE_LOCALES = { en: "en-GB", hi: "hi-IN", ar: "ar-EG-u-nu-latn", zh: "zh-CN", es: "es-ES" };

/** Data-driven article page. Slug derived from /insights/:slug. */
export default function ArticlePage() {
  const { pathname } = useLocation();
  const { t, data, lang } = useLang();
  const slug = pathname.split("/").filter(Boolean).pop();
  const a = getArticle(slug);
  if (!a) return <Navigate to="/insights" replace />;

  const da = data.articles?.[a.slug];
  const title = da?.title || a.title;
  const body = da?.body || a.body;
  const readTime = da?.readTime || a.readTime;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: a.title,
    description: a.excerpt,
    datePublished: a.date,
    image: a.image,
    author: { "@type": "Organization", name: "Farmsons" },
    publisher: { "@type": "Organization", name: "Farmsons", logo: { "@type": "ImageObject", url: `${SITE_URL}/farmsons-logo.png` } },
    mainEntityOfPage: `${SITE_URL}/insights/${a.slug}`,
  };

  return (
    <>
      <SEO title={`${a.title} | Farmsons Insights`} description={a.excerpt} image={a.image} jsonLd={schema} />
      <article className="pt-32 md:pt-40 pb-24 bg-[var(--cream)]">
        <div className="w-full max-w-[820px] mx-auto px-6 md:px-12">
          <Reveal>
            <Link to="/insights" className="inline-flex items-center gap-2 text-sm font-bold text-[#5f6b57] hover:text-[#d3a53a] transition-colors"><ArrowLeft size={16} /> {t("article.back")}</Link>
            <div className="mt-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.12em] text-[#d3a53a]">
              <span>{readTime}</span><span className="text-[#1d3a1f]/20">•</span>
              <time dateTime={a.date}>{new Date(a.date).toLocaleDateString(DATE_LOCALES[lang] || "en-GB", { day: "numeric", month: "long", year: "numeric" })}</time>
            </div>
            <h1 className="mt-4 font-display font-extrabold tracking-tight leading-[1.05] text-3xl md:text-5xl text-[#1d3a1f]">{title}</h1>
          </Reveal>
        </div>

        <Reveal delay={0.05}>
          <div className="w-full max-w-[1100px] mx-auto px-6 md:px-12 mt-10">
            <img src={a.image} alt={a.imageAlt} className="w-full h-[280px] md:h-[440px] object-cover rounded-2xl card-soft" />
          </div>
        </Reveal>

        <div className="w-full max-w-[820px] mx-auto px-6 md:px-12 mt-12">
          {body.map((sec, i) => (
            <Reveal key={i} delay={0.03 * i}>
              <section className="mb-10">
                <h2 className="font-display font-bold text-2xl md:text-3xl text-[#1d3a1f]">{sec.h}</h2>
                <p className="mt-4 text-[#3f4a3c] text-base md:text-lg leading-relaxed">{sec.p}</p>
              </section>
            </Reveal>
          ))}

          <div className="mt-12 pt-10 border-t border-[#1d3a1f]/10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <p className="font-display font-bold text-xl text-[#1d3a1f] max-w-sm">{t("article.cta")}</p>
            <Link to="/contact" className="group inline-flex items-center gap-2 bg-[#d3a53a] text-[#173a1e] font-bold px-7 py-3.5 rounded-full hover:bg-[#b98a26] transition-colors duration-200 shrink-0">
              {t("common.quote")} <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}
